'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import type { RefObject } from 'react'

/** Distancia a desplazar por click: el ancho de una tarjeta + su gap, medido
 * entre los dos primeros items del carril; con menos de dos, el ancho visible
 * del propio carril (mejor esfuerzo, evita dividir por datos inexistentes). */
function measureStep(rail: HTMLElement): number {
  const [first, second] = rail.children
  if (first instanceof HTMLElement && second instanceof HTMLElement) {
    return second.offsetLeft - first.offsetLeft
  }
  return rail.clientWidth
}

export interface UseRailScrollResult {
  /** Ref a colocar en el contenedor desplazable del carril. */
  readonly railRef: RefObject<HTMLDivElement | null>
  /** `true` si no queda nada que descubrir hacia la izquierda (o no desborda). */
  readonly atStart: boolean
  /** `true` si no queda nada que descubrir hacia la derecha (o no desborda). */
  readonly atEnd: boolean
  /** Recalcula `atStart`/`atEnd`. Conectar al `onScroll` del carril. */
  readonly recalc: () => void
  /** Desplaza un paso (una tarjeta) hacia adelante (`1`) o atrás (`-1`). */
  readonly scrollByStep: (direction: 1 | -1) => void
}

/**
 * Mecánica de un carril horizontal desplazable con flechas: mide el paso de
 * scroll, mantiene si estamos en cada extremo del recorrido y expone el
 * desplazamiento suave.
 *
 * Compartido por los módulos CMS que pintan un carril de producto
 * (`OtrosProductosIzq`, `ProductosBotonCompra`): la lógica de scroll es idéntica
 * en ambos y solo difiere qué tarjeta se pinta dentro de cada celda, así que el
 * hook aísla exactamente la parte común y deja la composición visual en cada
 * módulo (specs/031-productos-boton-compra-module/research.md §4; Constitución,
 * Principio III — hooks compartidos por ≥2 features viven en su propia carpeta
 * bajo `src/app/lib/hooks/`).
 *
 * Las flechas se ocultan en cada extremo del recorrido: `atStart`/`atEnd` se
 * recalculan en cada scroll y también cuando cambia el tamaño del carril
 * (`ResizeObserver`) o de la ventana — un resize puede hacer que el contenido
 * deje de desbordar (o empiece a hacerlo) sin que el usuario haya hecho scroll.
 * Ese es también el motivo de `itemsLength`: al cambiar el número de tarjetas
 * cambia el desbordamiento sin mediar scroll ni resize.
 */
export function useRailScroll(itemsLength: number): UseRailScrollResult {
  const railRef = useRef<HTMLDivElement>(null)
  const [atStart, setAtStart] = useState(true)
  const [atEnd, setAtEnd] = useState(true)

  const recalc = useCallback(() => {
    const el = railRef.current
    if (!el) return
    const max = el.scrollWidth - el.clientWidth
    if (max > 1) {
      setAtStart(el.scrollLeft <= 1)
      setAtEnd(el.scrollLeft >= max - 1)
    } else {
      // Sin desbordamiento (pocas tarjetas / rejilla móvil-tablet): nada que
      // descubrir en ningún sentido, ninguna flecha debe mostrarse.
      setAtStart(true)
      setAtEnd(true)
    }
  }, [])

  useEffect(() => {
    recalc()
    const el = railRef.current
    if (!el) return

    const resizeObserver = new ResizeObserver(recalc)
    resizeObserver.observe(el)
    window.addEventListener('resize', recalc)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', recalc)
    }
  }, [recalc, itemsLength])

  const scrollByStep = useCallback((direction: 1 | -1) => {
    const el = railRef.current
    if (!el) return
    el.scrollBy({ left: direction * measureStep(el), behavior: 'smooth' })
  }, [])

  return { railRef, atStart, atEnd, recalc, scrollByStep }
}
